import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import ApprovalQueue from '../components/ApprovalQueue';
import RejectedQueue from '../components/RejectedQueue';
import PushProgressBanner from '../components/PushProgressBanner';
import ProductsList from '../components/ProductsList';
import AttributeTierTab from '../components/AttributeTierTab';
import { 
  Play, 
  Pause, 
  Robot, 
  CheckCircle, 
  XCircle, 
  Clock,
  Eye,
  Trash,
  Lightning,
  FolderSimple,
  Sparkle,
  ArrowsClockwise,
  MagnifyingGlass,
  Tag
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Format seconds → human readable Norwegian duration ("~3t 12m", "~45m 12s", "~28s")
const formatDuration = (seconds) => {
  if (!isFinite(seconds) || seconds <= 0) return null;
  // Round up to whole minutes — user prefers minute-precision (sub-minute values
  // were too jittery to be useful on a 5-second polling loop).
  const totalMin = Math.max(1, Math.ceil(seconds / 60));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}t ${m}m`;
  return `${m}m`;
};

// Estimate seconds remaining. Uses observed throughput when available (more
// accurate — accounts for AI latency, WC API delays, etc.) and falls back to
// the configured rate per hour as a hard upper bound.
const computeEtaSeconds = (statusObj, ratePerHour) => {
  if (!statusObj?.running) return null;
  const total = statusObj.total_products ?? statusObj.total ?? 0;
  const processed = statusObj.processed ?? 0;
  const remaining = total - processed;
  if (remaining <= 0) return null;

  // Primary: observed rate from started_at + processed count
  if (statusObj.started_at && processed >= 3) {
    const elapsedSec = (Date.now() - new Date(statusObj.started_at).getTime()) / 1000;
    if (elapsedSec > 10) {
      const observedPerSec = processed / elapsedSec;
      if (observedPerSec > 0) return remaining / observedPerSec;
    }
  }

  // Fallback: configured rate (used during first ~10s of a fresh run)
  if (ratePerHour && ratePerHour > 0) return remaining * (3600 / ratePerHour);
  return null;
};

const FUNCTION_LABELS = {
  meta_title: { label: 'Meta-tittel', desc: 'SEO-tittel for Google (50-60 tegn)' },
  meta_description: { label: 'Meta-beskrivelse', desc: 'Søkeresultat-tekst (120-155 tegn)' },
  short_description: { label: 'Kort beskrivelse', desc: 'Kundevendt tekst med maler' },
  description: { label: 'Produktbeskrivelse', desc: 'Lang SEO-optimalisert tekst (150+ ord)' },
  product_name: { label: 'Varenavn', desc: 'Oppdater navn (f.eks. legg til PDF)' },
  translate_danish: { label: 'Oversett dansk → norsk', desc: 'Konverter dansk tekst til bokmål' },
  delivery_info: { label: 'Leveringsinfo', desc: 'PDF/post-info på oppskrifter og pakker' },
  image_alt_text: { label: 'Alt-tekst bilder', desc: 'AI-generert alt-tekst for SEO' },
  tags: { label: 'Stikkord (tags)', desc: 'AI-genererer 4–6 relevante WooCommerce-tagger' },
};

export default function AutoGuardPage() {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [approvalCounts, setApprovalCounts] = useState({ product: 0, category: 0, term: 0, total: 0 });
  // Bumped whenever the parent wants the ApprovalQueue children to refetch
  // (e.g. after AutoGuard ticks, after a single approve/reject elsewhere).
  const [approvalRefreshKey, setApprovalRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);

  // Category state
  const [catStatus, setCatStatus] = useState(null);
  const [categories, setCategories] = useState([]);
  const [catMode, setCatMode] = useState('approval');
  const [catSearch, setCatSearch] = useState('');
  const [catSyncing, setCatSyncing] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({});
  const [catSuggestions, setCatSuggestions] = useState(null);
  const [catGenerating, setCatGenerating] = useState(false);

  // Attribute term state
  const [termStatus, setTermStatus] = useState(null);
  const [attrs, setAttrs] = useState([]);
  const [termMode, setTermMode] = useState('approval');
  const [termAttrFilter, setTermAttrFilter] = useState('all');

  // WP Helper plugin status (needed for Yoast/RankMath meta on categories + terms)
  const [helperStatus, setHelperStatus] = useState(null);

  // Scope filter — applies to both AutoGuard's product picker AND the Produkter-tab list.
  const [scope, setScope] = useState({ issue: '', status: '', category: '', leverandor: '' });
  const [filterOpts, setFilterOpts] = useState({ categories: [], leverandorer: [] });

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  // Initial fetch + 5s status polling.
  useEffect(() => {
    fetchAll();
    const interval = setInterval(() => {
      fetchStatus();
      fetchCatStatus();
      fetchTermStatus();
      // Cheap counts-only poll keeps the badges fresh and tells children to
      // refetch their (paginated) lists.
      fetchApprovalCounts();
      bumpApprovalRefresh();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Re-fetch category/leverandør counts whenever the issue or status filter
  // changes — so the dropdowns reflect "antall som trenger behandling" for the
  // currently selected problem instead of the full catalogue.
  // Note: `fetchFilterOpts` is intentionally omitted from deps (it's declared
  // further down — referencing it here would trip the temporal-dead-zone
  // check). The hook depends on scope.issue/status which are the inputs that
  // change the underlying value.
  useEffect(() => {
    fetchFilterOpts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.issue, scope.status]);

  // While AutoGuard is processing, the counts decrease as products are fixed.
  // Refresh filter options every 15s so the dropdown numbers visibly tick down.
  useEffect(() => {
    if (!status?.running && !catStatus?.running && !termStatus?.running) return;
    const interval = setInterval(() => {
      fetchFilterOpts();
    }, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.running, catStatus?.running, termStatus?.running]);

  const fetchAll = async () => {
    await Promise.all([
      fetchSettings(), fetchStatus(), fetchLogs(), fetchApprovalCounts(),
      fetchCatStatus(), fetchCategories(),
      fetchTermStatus(), fetchAttrs(),
      fetchHelperStatus(),
      fetchFilterOpts(),
    ]);
    setLoading(false);
  };

  const fetchHelperStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/tenants/wp-helper-status`, { headers: getHeaders() });
      setHelperStatus(data);
    } catch {}
  };

  const fetchCatStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/autoguard/categories/status`, { headers: getHeaders() });
      setCatStatus(data);
    } catch {}
  };

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/api/taxonomy/categories`, { headers: getHeaders() });
      setCategories(data || []);
    } catch {}
  };

  const fetchTermStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/autoguard/terms/status`, { headers: getHeaders() });
      setTermStatus(data);
    } catch {}
  };

  const fetchAttrs = async () => {
    try {
      const { data } = await axios.get(`${API}/api/attributes`, { headers: getHeaders() });
      setAttrs(data || []);
    } catch {}
  };

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/api/autoguard/settings`, { headers: getHeaders() });
      setSettings(data);
      if (data.scope) setScope({ ...{ issue: '', status: '', category: '', leverandor: '' }, ...data.scope });
    } catch {}
  };

  const fetchFilterOpts = useCallback(async () => {
    try {
      // Pass the current issue + status so counts reflect "antall som trenger
      // behandling" rather than total catalogue size. When no issue is chosen
      // we fall back to whole-catalogue counts.
      const params = new URLSearchParams();
      if (scope.issue) params.set('issue', scope.issue);
      if (scope.status) params.set('status', scope.status);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const [c, l] = await Promise.all([
        axios.get(`${API}/api/products/categories/list${qs}`, { headers: getHeaders() }),
        axios.get(`${API}/api/products/leverandorer/list${qs}`, { headers: getHeaders() }),
      ]);
      setFilterOpts({ categories: c.data, leverandorer: l.data });
    } catch {}
  }, [getHeaders, scope.issue, scope.status]);

  const updateScope = async (field, value) => {
    const next = { ...scope, [field]: value === 'all' ? '' : value };
    setScope(next);
    try {
      await axios.put(`${API}/api/autoguard/settings`, { scope: next }, { headers: getHeaders() });
    } catch {}
  };

  const clearScope = async () => {
    const empty = { issue: '', status: '', category: '', leverandor: '' };
    setScope(empty);
    try {
      await axios.put(`${API}/api/autoguard/settings`, { scope: empty }, { headers: getHeaders() });
    } catch {}
  };

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/autoguard/status`, { headers: getHeaders() });
      setStatus(data);
    } catch {}
  };

  const fetchLogs = async () => {
    try {
      const { data } = await axios.get(`${API}/api/autoguard/log?limit=30`, { headers: getHeaders() });
      setLogs(data);
    } catch {}
  };

  const fetchApprovalCounts = async () => {
    try {
      const { data } = await axios.get(`${API}/api/autoguard/approval-counts`, { headers: getHeaders() });
      setApprovalCounts(data);
    } catch {}
  };

  // Tell child ApprovalQueue components to refetch (cheap — paginated, 50 items max).
  const bumpApprovalRefresh = () => setApprovalRefreshKey((k) => k + 1);

  const updateSetting = async (funcKey, field, value) => {
    const updated = { ...settings };
    updated.functions[funcKey][field] = value;
    setSettings(updated);
    try {
      await axios.put(`${API}/api/autoguard/settings`, { functions: updated.functions }, { headers: getHeaders() });
    } catch {}
  };

  const updateRateLimit = async (newRate) => {
    const rate = parseInt(newRate, 10);
    setSettings({ ...settings, rate_limit: rate });
    try {
      await axios.put(`${API}/api/autoguard/settings`, { rate_limit: rate }, { headers: getHeaders() });
      toast.success(`Hastighet endret til ${rate} produkter/time`);
    } catch {
      toast.error('Kunne ikke endre hastighet');
    }
  };

  const updateRegenPolicy = async (policy) => {
    // Optimistic update — toggle highlights instantly while the PUT settles.
    setSettings({ ...settings, regen_policy: policy });
    try {
      await axios.put(`${API}/api/autoguard/settings`, { regen_policy: policy }, { headers: getHeaders() });
      toast.success(policy === 'max'
        ? 'Aktivert Max kvalitet — AutoGuard regenererer alle felt'
        : 'Aktivert Kostnadskontroll — AutoGuard regenererer bare det som mangler');
    } catch {
      toast.error('Kunne ikke lagre valg');
      // Roll back on failure
      setSettings((prev) => ({ ...prev, regen_policy: prev?.regen_policy === 'max' ? 'smart' : 'max' }));
    }
  };

  const startAutoGuard = async () => {
    try {
      const { data } = await axios.post(`${API}/api/autoguard/start`, {}, { headers: getHeaders() });
      toast.success(`AutoGuard startet - ${data.total_products} produkter i kø`);
      fetchStatus();
    } catch (err) {
      if (err.response?.status === 409) toast.error('AutoGuard kjører allerede');
      else toast.error('Kunne ikke starte');
    }
  };

  const pauseAutoGuard = async () => {
    try {
      await axios.post(`${API}/api/autoguard/pause`, {}, { headers: getHeaders() });
      toast.success('AutoGuard pauset');
      fetchStatus();
    } catch { toast.error('Kunne ikke pause'); }
  };

  // --- Category AutoGuard handlers ---
  const startCatAutoguard = async () => {
    try {
      const { data } = await axios.post(`${API}/api/autoguard/categories/start`,
        { mode: catMode, only_missing: true },
        { headers: getHeaders() });
      if (data.total === 0) toast.info(data.message);
      else toast.success(`Startet på ${data.total} kategorier (${data.mode})`);
      fetchCatStatus();
    } catch (err) {
      if (err.response?.status === 409) toast.error('Kjører allerede');
      else toast.error('Kunne ikke starte');
    }
  };

  const pauseCatAutoguard = async () => {
    try {
      await axios.post(`${API}/api/autoguard/categories/pause`, {}, { headers: getHeaders() });
      toast.success('Pauset');
      fetchCatStatus();
    } catch { toast.error('Feilet'); }
  };

  const resetCatProgress = async () => {
    if (!window.confirm('Nullstill fremgang? Alle kategorier blir plukket opp på nytt ved neste start.')) return;
    try {
      const { data } = await axios.post(`${API}/api/autoguard/categories/reset`, {}, { headers: getHeaders() });
      toast.success(data.message);
      fetchCatStatus();
    } catch { toast.error('Feilet'); }
  };

  const syncCategories = async () => {
    setCatSyncing(true);
    try {
      await axios.post(`${API}/api/taxonomy/categories/sync`, {}, { headers: getHeaders() });
      toast.success('Sync startet – henter fra WooCommerce');
      setTimeout(async () => { await fetchCategories(); setCatSyncing(false); }, 6000);
    } catch { toast.error('Sync feilet'); setCatSyncing(false); }
  };

  const openCatEdit = (cat) => {
    setEditingCat(cat);
    setCatForm({
      name: cat.name || '',
      description: cat.description || '',
      meta_title: cat.meta_title || '',
      meta_description: cat.meta_description || '',
    });
    setCatSuggestions(null);
  };

  const generateCatAi = async () => {
    if (!editingCat) return;
    setCatGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/api/taxonomy/categories/${editingCat.id}/optimize`, {},
        { headers: getHeaders() }
      );
      setCatSuggestions(data.suggestions);
      toast.success('AI-forslag klart');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI feilet');
    } finally { setCatGenerating(false); }
  };

  const applyCatSuggestions = () => {
    if (!catSuggestions) return;
    setCatForm({
      ...catForm,
      description: catSuggestions.description || catForm.description,
      meta_title: catSuggestions.meta_title || catForm.meta_title,
      meta_description: catSuggestions.meta_description || catForm.meta_description,
    });
    toast.success('Forslag satt inn');
  };

  const saveCatChanges = async () => {
    if (!editingCat) return;
    try {
      await axios.post(
        `${API}/api/taxonomy/categories/${editingCat.id}/apply`,
        catForm,
        { headers: getHeaders() }
      );
      toast.success('Lagret lokalt');
      setEditingCat(null);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lagring feilet');
    }
  };

  const pushCatToWoo = async () => {
    if (!editingCat) return;
    try {
      // Save first (in case user edited fields)
      await axios.post(
        `${API}/api/taxonomy/categories/${editingCat.id}/apply`,
        catForm,
        { headers: getHeaders() }
      );
      const { data } = await axios.post(
        `${API}/api/taxonomy/categories/${editingCat.id}/push`, {},
        { headers: getHeaders() }
      );
      toast.success(data.message || 'Pushet til WooCommerce');
      setEditingCat(null);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Push feilet');
    }
  };

  // Push-all background job tracking
  const [catPushJob, setCatPushJob] = useState(null);
  const [termPushJob, setTermPushJob] = useState(null);

  const pollPushJob = async (jobId, kind) => {
    const setter = kind === 'categories' ? setCatPushJob : setTermPushJob;
    try {
      const { data } = await axios.get(`${API}/api/taxonomy/push-progress/${jobId}`, { headers: getHeaders() });
      setter(data);
      if (data.status === 'done') {
        const msg = `Pushet ${data.pushed} av ${data.total} ${kind === 'categories' ? 'kategorier' : 'termer'}${data.failed ? ` (${data.failed} feilet)` : ''}`;
        if (data.failed > 0) toast.warning(msg); else toast.success(msg);
        if (kind === 'categories') fetchCategories(); else fetchAttrs();
        setTimeout(() => setter(null), 8000); // hide banner after 8s
        return true; // stop polling
      }
    } catch {
      // ignore — try again next tick
    }
    return false;
  };

  useEffect(() => {
    if (!catPushJob || catPushJob.status !== 'running') return;
    const t = setInterval(async () => {
      const done = await pollPushJob(catPushJob.job_id, 'categories');
      if (done) clearInterval(t);
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catPushJob?.job_id, catPushJob?.status]);

  useEffect(() => {
    if (!termPushJob || termPushJob.status !== 'running') return;
    const t = setInterval(async () => {
      const done = await pollPushJob(termPushJob.job_id, 'terms');
      if (done) clearInterval(t);
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termPushJob?.job_id, termPushJob?.status]);

  const pushAllCats = async () => {
    if (!window.confirm('Push alle lokalt endrede kategorier til WooCommerce?')) return;
    try {
      const { data } = await axios.post(`${API}/api/taxonomy/categories/push-all`, {}, { headers: getHeaders() });
      if (data.status === 'noop') {
        toast.info(data.message || 'Ingen kategorier å pushe');
        return;
      }
      toast.success(data.message || `${data.queued} kategorier behandles i bakgrunnen`);
      // Seed progress and start polling via useEffect
      setCatPushJob({
        job_id: data.job_id, kind: 'categories', total: data.queued,
        pushed: 0, failed: 0, status: 'running', current_name: null,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Push-all feilet');
    }
  };

  // --- Term AutoGuard handlers ---
  const startTermAutoguard = async () => {
    try {
      const body = { mode: termMode };
      if (termAttrFilter !== 'all') body.attribute_id = parseInt(termAttrFilter, 10);
      const { data } = await axios.post(`${API}/api/autoguard/terms/start`, body, { headers: getHeaders() });
      if (data.total === 0) toast.info(data.message);
      else toast.success(`Startet på ${data.total} termer (${data.mode})`);
      fetchTermStatus();
    } catch (err) {
      if (err.response?.status === 409) toast.error('Kjører allerede');
      else toast.error('Kunne ikke starte');
    }
  };

  const pauseTermAutoguard = async () => {
    try {
      await axios.post(`${API}/api/autoguard/terms/pause`, {}, { headers: getHeaders() });
      toast.success('Pauset');
      fetchTermStatus();
    } catch { toast.error('Feilet'); }
  };

  const resetTermProgress = async () => {
    if (!window.confirm('Nullstill fremgang for alle termer?')) return;
    try {
      const { data } = await axios.post(`${API}/api/autoguard/terms/reset`, {}, { headers: getHeaders() });
      toast.success(data.message);
      fetchTermStatus();
    } catch { toast.error('Feilet'); }
  };

  const pushAllTerms = async () => {
    if (!window.confirm('Push alle lokalt endrede termer til WooCommerce?')) return;
    try {
      const { data } = await axios.post(`${API}/api/taxonomy/attributes/terms/push-all`, {}, { headers: getHeaders() });
      if (data.status === 'noop') {
        toast.info(data.message || 'Ingen termer å pushe');
        return;
      }
      toast.success(data.message || `${data.queued} termer behandles i bakgrunnen`);
      setTermPushJob({
        job_id: data.job_id, kind: 'terms', total: data.queued,
        pushed: 0, failed: 0, status: 'running', current_name: null,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Push-all feilet');
    }
  };

  if (loading) {
    return <div className="space-y-4"><div className="h-8 w-48 bg-muted animate-pulse rounded" /><div className="h-64 bg-muted animate-pulse rounded-md" /></div>;
  }

  const progress = status?.total_products > 0 
    ? Math.round((status.processed / status.total_products) * 100) 
    : 0;

  const productEtaSec = computeEtaSeconds(status, settings?.rate_limit);
  const productEtaLabel = formatDuration(productEtaSec);
  const catEtaSec = computeEtaSeconds(catStatus, settings?.rate_limit);
  const catEtaLabel = formatDuration(catEtaSec);
  const termEtaSec = computeEtaSeconds(termStatus, settings?.rate_limit);
  const termEtaLabel = formatDuration(termEtaSec);

  const catProgress = catStatus?.total > 0 ? Math.round((catStatus.processed / catStatus.total) * 100) : 0;
  const filteredCats = categories.filter((c) =>
    !catSearch || c.name?.toLowerCase().includes(catSearch.toLowerCase()) || c.slug?.toLowerCase().includes(catSearch.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="autoguard-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-4xl tracking-tight font-extrabold">AutoGuard</h1>
        <p className="text-sm text-muted-foreground mt-1">Automatisert SEO-optimalisering i bakgrunnen</p>
      </div>

      {/* Scope tabs: Produkter | Kategorier */}
      <Tabs defaultValue="products" className="space-y-4">
        <div className="overflow-x-auto -mx-1 px-1 pb-1 [-webkit-overflow-scrolling:touch]">
          <TabsList className="bg-muted/60 w-max" data-testid="scope-tabs">
            <TabsTrigger value="products" className="gap-1" data-testid="scope-products"><Robot size={14} /> Produkter</TabsTrigger>
            <TabsTrigger value="categories" className="gap-1" data-testid="scope-categories"><FolderSimple size={14} /> Kategorier</TabsTrigger>
            <TabsTrigger value="terms" className="gap-1" data-testid="scope-terms"><Tag size={14} /> Attributter</TabsTrigger>
          </TabsList>
        </div>

        {/* ======================= PRODUCTS SCOPE ======================= */}
        <TabsContent value="products" className="space-y-6">
          {/* Scope filter — applies to AutoGuard's queue AND the Produkter-tab list */}
          <Card className="border border-border" data-testid="scope-filter-card">
            <CardContent className="p-4">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-xs font-semibold tracking-wide uppercase text-muted-foreground mb-2">🎯 Avgrensning</p>
                  <p className="text-xs text-muted-foreground">
                    Filtrer hvilke produkter AutoGuard og listen jobber med. Tom = hele katalogen.
                  </p>
                </div>
                <Select value={scope.issue || 'all'} onValueChange={(v) => updateScope('issue', v)}>
                  <SelectTrigger className="w-full sm:w-[200px]" data-testid="scope-issue"><SelectValue placeholder="Problem" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle problemer</SelectItem>
                    <SelectItem value="missing_description">Mangler beskrivelse</SelectItem>
                    <SelectItem value="missing_short_description">Mangler kort beskr.</SelectItem>
                    <SelectItem value="missing_images">Mangler bilder</SelectItem>
                    <SelectItem value="low_seo">Lav SEO-score</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={scope.status || 'all'} onValueChange={(v) => updateScope('status', v)}>
                  <SelectTrigger className="w-full sm:w-[150px]" data-testid="scope-status"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle statuser</SelectItem>
                    <SelectItem value="publish">Publisert</SelectItem>
                    <SelectItem value="draft">Utkast</SelectItem>
                    <SelectItem value="private">Privat</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={scope.category || 'all'} onValueChange={(v) => updateScope('category', v)}>
                  <SelectTrigger className="w-full sm:w-[180px]" data-testid="scope-category"><SelectValue placeholder="Kategori" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle kategorier</SelectItem>
                    {filterOpts.categories.map((c) => (
                      <SelectItem key={c.name} value={c.name}>{c.name} ({c.count})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={scope.leverandor || 'all'} onValueChange={(v) => updateScope('leverandor', v)}>
                  <SelectTrigger className="w-full sm:w-[180px]" data-testid="scope-leverandor"><SelectValue placeholder="Leverandør" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle leverandører</SelectItem>
                    {filterOpts.leverandorer.map((l) => (
                      <SelectItem key={l.name} value={l.name}>{l.name} ({l.count})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(scope.issue || scope.status || scope.category || scope.leverandor) && (
                  <Button variant="outline" size="sm" onClick={clearScope} data-testid="clear-scope-btn">Nullstill</Button>
                )}
                {status?.running ? (
                  <Button variant="outline" onClick={pauseAutoGuard} data-testid="pause-autoguard-btn" className="gap-2 ml-auto">
                    <Pause size={14} weight="bold" /> Pause
                  </Button>
                ) : (
                  <Button onClick={startAutoGuard} data-testid="start-autoguard-btn" className="gap-2 ml-auto">
                    <Play size={14} weight="bold" /> Start AutoGuard
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

      {/* Status Card */}
      <Card className="border border-border" data-testid="autoguard-status">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <div className={`w-3 h-3 rounded-full ${status?.running ? 'bg-success animate-pulse' : 'bg-muted-foreground'}`} />
            <span className="text-sm font-medium">{status?.running ? 'Kjører' : status?.paused ? 'Pauset' : 'Stoppet'}</span>
            {status?.running && <span className="text-xs text-muted-foreground">~{settings?.rate_limit ?? 50} produkter/time</span>}
            {productEtaLabel && (
              <span className="text-xs font-medium text-foreground bg-muted/60 px-2 py-1 rounded ml-auto" data-testid="product-eta">
                Estimert tid igjen: <span className="font-mono">~{productEtaLabel}</span>
              </span>
            )}
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>{status?.processed || 0} av {status?.total_products || 0} behandlet</span>
              <span className="font-mono">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="text-center p-3 bg-muted/50 rounded-md">
              <p className="font-heading text-xl font-bold text-success">{status?.auto_applied || 0}</p>
              <p className="text-xs text-muted-foreground">Auto-endret</p>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-md">
              <p className="font-heading text-xl font-bold text-warning">{status?.pending_approval || 0}</p>
              <p className="text-xs text-muted-foreground">Venter godkjenning</p>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-md">
              <p className="font-heading text-xl font-bold text-destructive">{status?.errors || 0}</p>
              <p className="text-xs text-muted-foreground">Feil</p>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-md">
              <p className="font-heading text-xl font-bold">{(status?.total_products || 0) - (status?.processed || 0)}</p>
              <p className="text-xs text-muted-foreground">Gjenstår{productEtaLabel ? ` · ~${productEtaLabel}` : ''}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs: Produkter / Settings / Approval Queue / Log */}
      <Tabs defaultValue="produkter" className="space-y-4">
        <div className="overflow-x-auto -mx-1 px-1 pb-1 [-webkit-overflow-scrolling:touch]">
          <TabsList className="w-max" data-testid="autoguard-tabs">
            <TabsTrigger value="produkter" data-testid="tab-products">Produkter</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Innstillinger</TabsTrigger>
            <TabsTrigger value="approvals" className="gap-1" data-testid="tab-approvals">
              Godkjenning
              {approvalCounts.total > 0 && <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 text-xs flex items-center justify-center">{approvalCounts.total}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">Avviste forslag</TabsTrigger>
            <TabsTrigger value="log" data-testid="tab-log">Endringslogg</TabsTrigger>
          </TabsList>
        </div>

        {/* Produkter tab */}
        <TabsContent value="produkter" className="space-y-4">
          <ProductsList filters={scope} />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card className="border border-border" data-testid="regen-policy-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <span>🎯</span> Regen-strategi
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Velg om AutoGuard skal regenerere alt for hvert produkt, eller bare når et felt er tomt/feil lengde.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => updateRegenPolicy('max')}
                  data-testid="regen-policy-max"
                  className={`text-left p-4 border-2 rounded-md transition-colors ${
                    (settings?.regen_policy ?? 'max') === 'max'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/40'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base">🚀</span>
                    <span className="font-medium">Max kvalitet</span>
                    {(settings?.regen_policy ?? 'max') === 'max' && (
                      <span className="ml-auto text-xs uppercase tracking-wide text-primary">Aktiv</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Regenererer ALLE aktiverte felt på hvert produkt — også de som allerede er fylt. Polishing-pass kjører for å sikre at output passerer hele sjekklisten. Bruker ~2× mer AI-tokens, men løfter lavt-skårende produkter optimalt.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => updateRegenPolicy('smart')}
                  data-testid="regen-policy-smart"
                  className={`text-left p-4 border-2 rounded-md transition-colors ${
                    settings?.regen_policy === 'smart'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/40'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base">💰</span>
                    <span className="font-medium">Kostnadskontroll</span>
                    {settings?.regen_policy === 'smart' && (
                      <span className="ml-auto text-xs uppercase tracking-wide text-primary">Aktiv</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Regenererer bare når et felt mangler eller er utenfor optimal lengde. Sparer AI-tokens, men "ok men ikke optimale" produkter forblir uendret. Bra når katalogen allerede er stort sett ferdig.
                  </p>
                </button>
              </div>
            </CardContent>
          </Card>


          <Card className="border border-border" data-testid="rate-limit-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <span>⚡</span> Hastighet
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Hvor mange produkter AutoGuard skal behandle per time. Endring trer i kraft umiddelbart — også mens jobben kjører.
                <br />
                <strong>Tips:</strong> Kjør raskt (200/t) for å gå gjennom hele katalogen, deretter "Re-optimaliser" lav-CTR-sider via SEO Insights for å finjustere.
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <Label className="text-sm font-medium">Produkter per time</Label>
                  <p className="text-xs text-muted-foreground">
                    {settings?.rate_limit
                      ? `Ca. ${(3600 / settings.rate_limit).toFixed(0)} sekund mellom hvert produkt`
                      : 'Velg hastighet'}
                  </p>
                </div>
                <Select
                  value={String(settings?.rate_limit ?? 50)}
                  onValueChange={updateRateLimit}
                >
                  <SelectTrigger className="w-full sm:w-[180px]" data-testid="rate-limit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">🐢 Forsiktig — 25/t</SelectItem>
                    <SelectItem value="50">🚶 Normal — 50/t (anbefalt)</SelectItem>
                    <SelectItem value="100">🏃 Rask — 100/t</SelectItem>
                    <SelectItem value="200">⚡ Lyn — 200/t</SelectItem>
                    <SelectItem value="500">🚀 Maks — 500/t</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-3 p-3 bg-muted/30 rounded-md text-xs text-muted-foreground">
                <strong className="text-foreground">Merk:</strong> Kvaliteten på hvert produkt er identisk uansett hastighet — AI-modellen og prompten er den samme. Hastighet påvirker kun hvor raskt køen tømmes. Lavere fart belaster Woo + AI-API-er mindre.
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg">SEO-funksjoner</CardTitle>
              <p className="text-xs text-muted-foreground">Velg modus for hver funksjon: <strong>Auto</strong> = AI endrer direkte | <strong>Godkjenning</strong> = havner i godkjenningskø</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {settings?.functions && Object.entries(settings.functions).map(([key, config]) => (
                <div key={key} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={config.enabled}
                      onCheckedChange={(v) => updateSetting(key, 'enabled', v)}
                      data-testid={`toggle-${key}`}
                    />
                    <div>
                      <Label className="text-sm font-medium">{FUNCTION_LABELS[key]?.label || key}</Label>
                      <p className="text-xs text-muted-foreground">{FUNCTION_LABELS[key]?.desc || ''}</p>
                    </div>
                  </div>
                  <Select 
                    value={config.mode} 
                    onValueChange={(v) => updateSetting(key, 'mode', v)}
                    disabled={!config.enabled}
                  >
                    <SelectTrigger className="w-[140px]" data-testid={`mode-${key}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto</SelectItem>
                      <SelectItem value="approval">Godkjenning</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Approval Queue Tab */}
        <TabsContent value="approvals" className="space-y-4">
          <ApprovalQueue
            kind="product"
            getHeaders={getHeaders}
            refreshKey={approvalRefreshKey}
            onChanged={() => { fetchApprovalCounts(); fetchLogs(); fetchStatus(); }}
          />
        </TabsContent>

        {/* Rejected Tab */}
        <TabsContent value="rejected" className="space-y-4">
          <RejectedQueue />
        </TabsContent>

        {/* Log Tab */}
        <TabsContent value="log" className="space-y-4">
          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Siste endringer</CardTitle>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Ingen endringer ennå. Start AutoGuard for å begynne.</p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {logs.map((log, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <div className="flex items-center gap-2">
                        {log.action === 'auto_applied' ? (
                          <Lightning size={14} className="text-success" />
                        ) : log.action === 'approved_and_pushed' ? (
                          <CheckCircle size={14} className="text-primary" />
                        ) : (
                          <Clock size={14} className="text-muted-foreground" />
                        )}
                        <div>
                          <p className="text-sm truncate max-w-[300px]">{log.product_name}</p>
                          <p className="text-xs text-muted-foreground">{log.changes?.filter(c => !['modified_locally','local_modified_at','autoguard_auto','seo_score'].includes(c)).join(', ')}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant={log.action === 'auto_applied' ? 'default' : 'outline'} className="text-xs">
                          {log.action === 'auto_applied' ? 'Auto' : 'Godkjent'}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString('nb-NO', {hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short'}) : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
        </TabsContent>

        {/* ======================= CATEGORIES SCOPE ======================= */}
        <TabsContent value="categories" className="space-y-6">
          {/* Live push-all progress */}
          <PushProgressBanner job={catPushJob} label="Pusher kategorier til WooCommerce" />

          {/* WP Helper plugin warning */}
          {helperStatus && !helperStatus.installed && (
            <Card className="border-2 border-destructive bg-destructive/5" data-testid="helper-missing-banner-cat">
              <CardContent className="p-4 flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-[280px]">
                  <div className="p-2 bg-destructive/20 rounded-md shrink-0">
                    <XCircle size={20} weight="bold" className="text-destructive" />
                  </div>
                  <div>
                    <p className="font-heading text-base font-bold">
                      Yoast SEO meta blir ikke lagret på kategorier og termer
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Sortilize genererer riktig meta-tittel og beskrivelse, men WordPress
                      blokkerer skriving av Yoast/RankMath meta-felter via REST API.{' '}
                      <strong>Last ned og installer "Sortilize Helper"-plugin</strong> på WordPress for å løse det.
                      Beskrivelsen pushes uansett, men meta-tittel/beskrivelse krever plugin.
                    </p>
                  </div>
                </div>
                <a
                  href="/sortilize-helper.zip"
                  download
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 text-sm font-medium shrink-0"
                  data-testid="download-helper-plugin"
                >
                  Last ned plugin
                </a>
              </CardContent>
            </Card>
          )}

          {/* Prominent approval banner */}
          {approvalCounts.category > 0 && (
            <Card className="border-2 border-warning bg-warning/5" data-testid="cat-approval-banner">
              <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-warning/20 rounded-md">
                    <CheckCircle size={20} weight="bold" className="text-warning" />
                  </div>
                  <div>
                    <p className="font-heading text-base font-bold">
                      {approvalCounts.category} kategorier venter på godkjenning
                    </p>
                    <p className="text-xs text-muted-foreground">Klikk for å se alle og godkjenne i bulk</p>
                  </div>
                </div>
                <Button
                  onClick={() => document.getElementById('cat-approval-queue')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="gap-2"
                  data-testid="goto-cat-approvals"
                >
                  Vis godkjenningskø <ArrowsClockwise size={14} />
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <Select value={catMode} onValueChange={setCatMode} disabled={catStatus?.running}>
                <SelectTrigger className="w-[180px]" data-testid="cat-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approval">Modus: Godkjenning</SelectItem>
                  <SelectItem value="auto">Modus: Auto</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">Kun kategorier som mangler meta/beskrivelse</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={syncCategories} disabled={catSyncing} className="gap-2" data-testid="sync-cat-btn">
                <ArrowsClockwise size={14} className={catSyncing ? 'animate-spin' : ''} />
                {catSyncing ? 'Synker...' : 'Sync fra Woo'}
              </Button>
              <Button variant="outline" onClick={pushAllCats} className="gap-2" data-testid="push-all-cat-btn">
                Push alle til Woo
              </Button>
              <Button variant="outline" onClick={resetCatProgress} disabled={catStatus?.running} className="gap-2" data-testid="reset-cat-btn">
                <Trash size={14} /> Nullstill
              </Button>
              {catStatus?.running ? (
                <Button variant="outline" onClick={pauseCatAutoguard} data-testid="pause-cat-btn" className="gap-2">
                  <Pause size={14} weight="bold" /> Pause
                </Button>
              ) : (
                <Button onClick={startCatAutoguard} data-testid="start-cat-btn" className="gap-2">
                  <Play size={14} weight="bold" /> Start for kategorier
                </Button>
              )}
            </div>
          </div>

          {/* Status Card */}
          <Card className="border border-border" data-testid="cat-status-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4 flex-wrap">
                <div className={`w-3 h-3 rounded-full ${catStatus?.running ? 'bg-success animate-pulse' : 'bg-muted-foreground'}`} />
                <span className="text-sm font-medium">{catStatus?.running ? 'Kjører' : catStatus?.paused ? 'Pauset' : 'Stoppet'}</span>
                {catStatus?.running && <span className="text-xs text-muted-foreground">~{settings?.rate_limit ?? 50} kategorier/time</span>}
                {catEtaLabel && (
                  <span className="text-xs font-medium text-foreground bg-muted/60 px-2 py-1 rounded ml-auto" data-testid="cat-eta">
                    Estimert tid igjen: <span className="font-mono">~{catEtaLabel}</span>
                  </span>
                )}
              </div>

              {(catStatus?.total || 0) > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{catStatus?.processed || 0} av {catStatus?.total || 0} behandlet</span>
                    <span className="font-mono">{catProgress}%</span>
                  </div>
                  <Progress value={catProgress} className="h-2" />
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div className="text-center p-3 bg-muted/50 rounded-md">
                  <p className="font-heading text-xl font-bold text-success">{catStatus?.auto_applied || 0}</p>
                  <p className="text-xs text-muted-foreground">Auto-endret</p>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-md">
                  <p className="font-heading text-xl font-bold text-warning">{catStatus?.pending_approval || 0}</p>
                  <p className="text-xs text-muted-foreground">Venter godkjenning</p>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-md">
                  <p className="font-heading text-xl font-bold text-destructive">{catStatus?.errors || 0}</p>
                  <p className="text-xs text-muted-foreground">Feil</p>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-md">
                  <p className="font-heading text-xl font-bold">{categories.length}</p>
                  <p className="text-xs text-muted-foreground">Kategorier totalt</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Approvals for categories */}
          {approvalCounts.category > 0 && (
            <Card id="cat-approval-queue" className="border border-border scroll-mt-24">
              <CardContent className="p-4">
                <p className="font-heading text-base flex items-center gap-2 mb-3">
                  Kategori-endringer til godkjenning
                  <Badge variant="destructive" className="text-xs">{approvalCounts.category}</Badge>
                </p>
                <ApprovalQueue
                  kind="category"
                  getHeaders={getHeaders}
                  refreshKey={approvalRefreshKey}
                  onChanged={() => { fetchApprovalCounts(); fetchLogs(); fetchCatStatus(); fetchCategories(); }}
                />
              </CardContent>
            </Card>
          )}

          {/* Manual category list */}
          <Card className="border border-border">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Tag size={16} /> Alle kategorier ({filteredCats.length})
              </CardTitle>
              <div className="relative mt-2">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Søk kategori..."
                  value={catSearch}
                  onChange={(e) => setCatSearch(e.target.value)}
                  className="pl-9 h-8 text-sm"
                  data-testid="cat-search-input"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0 max-h-[500px] overflow-y-auto">
              {filteredCats.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  Ingen kategorier. Klikk "Sync fra Woo" for å hente fra WooCommerce.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {filteredCats.map((cat) => {
                    const hasMeta = !!(cat.meta_title && cat.meta_description);
                    return (
                      <div
                        key={cat.id}
                        className="flex items-center justify-between p-3 hover:bg-muted/20 cursor-pointer"
                        onClick={() => openCatEdit(cat)}
                        data-testid={`cat-list-row-${cat.id}`}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{cat.name}</span>
                            <Badge variant="outline" className="text-xs">{cat.count || 0} prod.</Badge>
                            {cat.modified_locally && <Badge variant="default" className="text-xs gap-1"><CheckCircle size={10} /> Optimalisert</Badge>}
                          </div>
                          <p className={`text-xs mt-0.5 ${hasMeta ? 'text-success' : 'text-destructive'}`}>
                            {hasMeta ? '✓ Meta på plass' : '✗ Mangler meta'} · /{cat.slug}
                          </p>
                        </div>
                        <Button size="sm" variant="outline" className="gap-1">
                          <Sparkle size={12} /> Rediger
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ======================= TERMS (ATTRIBUTES) SCOPE ======================= */}
        <TabsContent value="terms" className="space-y-6">
          {/* Live push-all progress */}
          <PushProgressBanner job={termPushJob} label="Pusher termer til WooCommerce" />

          {/* WP Helper plugin warning */}
          {helperStatus && !helperStatus.installed && (
            <Card className="border-2 border-destructive bg-destructive/5" data-testid="helper-missing-banner-term">
              <CardContent className="p-4 flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-[280px]">
                  <div className="p-2 bg-destructive/20 rounded-md shrink-0">
                    <XCircle size={20} weight="bold" className="text-destructive" />
                  </div>
                  <div>
                    <p className="font-heading text-base font-bold">
                      Yoast SEO meta blir ikke lagret på termer
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Last ned og installer <strong>Sortilize Helper</strong>-plugin på WordPress
                      for at meta-tittel og meta-beskrivelse skal lagres riktig.
                    </p>
                  </div>
                </div>
                <a
                  href="/sortilize-helper.zip"
                  download
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 text-sm font-medium shrink-0"
                >
                  Last ned plugin
                </a>
              </CardContent>
            </Card>
          )}

          {/* Prominent approval banner */}
          {approvalCounts.term > 0 && (
            <Card className="border-2 border-warning bg-warning/5" data-testid="term-approval-banner">
              <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-warning/20 rounded-md">
                    <CheckCircle size={20} weight="bold" className="text-warning" />
                  </div>
                  <div>
                    <p className="font-heading text-base font-bold">
                      {approvalCounts.term} termer venter på godkjenning
                    </p>
                    <p className="text-xs text-muted-foreground">Rediger tekster, godkjenn enkeltvis eller i bulk</p>
                  </div>
                </div>
                <Button
                  onClick={() => document.getElementById('term-approval-queue')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="gap-2"
                  data-testid="goto-term-approvals"
                >
                  Vis godkjenningskø <ArrowsClockwise size={14} />
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3 flex-wrap">
              <Select value={termMode} onValueChange={setTermMode} disabled={termStatus?.running}>
                <SelectTrigger className="w-[180px]" data-testid="term-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approval">Modus: Godkjenning</SelectItem>
                  <SelectItem value="auto">Modus: Auto (push til Woo)</SelectItem>
                </SelectContent>
              </Select>
              <Select value={termAttrFilter} onValueChange={setTermAttrFilter} disabled={termStatus?.running}>
                <SelectTrigger className="w-[200px]" data-testid="term-attr-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle attributter</SelectItem>
                  {attrs.map((a) => (
                    <SelectItem key={a.id} value={String(a.id)}>{a.name} ({a.terms?.length || 0})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">Kun termer som mangler meta/beskrivelse</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={pushAllTerms} className="gap-2" data-testid="push-all-terms-btn">
                Push alle til Woo
              </Button>
              <Button variant="outline" onClick={resetTermProgress} disabled={termStatus?.running} className="gap-2" data-testid="reset-term-btn">
                <Trash size={14} /> Nullstill
              </Button>
              {termStatus?.running ? (
                <Button variant="outline" onClick={pauseTermAutoguard} data-testid="pause-term-btn" className="gap-2">
                  <Pause size={14} weight="bold" /> Pause
                </Button>
              ) : (
                <Button onClick={startTermAutoguard} data-testid="start-term-btn" className="gap-2">
                  <Play size={14} weight="bold" /> Start for termer
                </Button>
              )}
            </div>
          </div>

          {/* Status Card */}
          <Card className="border border-border" data-testid="term-status-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4 flex-wrap">
                <div className={`w-3 h-3 rounded-full ${termStatus?.running ? 'bg-success animate-pulse' : 'bg-muted-foreground'}`} />
                <span className="text-sm font-medium">{termStatus?.running ? 'Kjører' : termStatus?.paused ? 'Pauset' : 'Stoppet'}</span>
                {termStatus?.running && <span className="text-xs text-muted-foreground">~{settings?.rate_limit ?? 50} termer/time</span>}
                {termEtaLabel && (
                  <span className="text-xs font-medium text-foreground bg-muted/60 px-2 py-1 rounded ml-auto" data-testid="term-eta">
                    Estimert tid igjen: <span className="font-mono">~{termEtaLabel}</span>
                  </span>
                )}
              </div>

              {(termStatus?.total || 0) > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{termStatus?.processed || 0} av {termStatus?.total || 0} behandlet</span>
                    <span className="font-mono">{termStatus?.total > 0 ? Math.round((termStatus.processed / termStatus.total) * 100) : 0}%</span>
                  </div>
                  <Progress value={termStatus?.total > 0 ? Math.round((termStatus.processed / termStatus.total) * 100) : 0} className="h-2" />
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div className="text-center p-3 bg-muted/50 rounded-md">
                  <p className="font-heading text-xl font-bold text-success">{termStatus?.auto_applied || 0}</p>
                  <p className="text-xs text-muted-foreground">Auto-endret</p>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-md">
                  <p className="font-heading text-xl font-bold text-warning">{termStatus?.pending_approval || 0}</p>
                  <p className="text-xs text-muted-foreground">Venter godkjenning</p>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-md">
                  <p className="font-heading text-xl font-bold text-destructive">{termStatus?.errors || 0}</p>
                  <p className="text-xs text-muted-foreground">Feil</p>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-md">
                  <p className="font-heading text-xl font-bold">
                    {attrs.reduce((sum, a) => sum + (a.terms?.length || 0), 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Termer totalt</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Index policy configurator — lives here since it's attribute-specific */}
          <AttributeTierTab getHeaders={getHeaders} />

          {/* Approvals for terms */}
          {approvalCounts.term > 0 && (
            <Card id="term-approval-queue" className="border border-border scroll-mt-24">
              <CardContent className="p-4">
                <p className="font-heading text-base flex items-center gap-2 mb-3">
                  Term-endringer til godkjenning
                  <Badge variant="destructive" className="text-xs">{approvalCounts.term}</Badge>
                </p>
                <ApprovalQueue
                  kind="term"
                  getHeaders={getHeaders}
                  refreshKey={approvalRefreshKey}
                  onChanged={() => { fetchApprovalCounts(); fetchLogs(); fetchTermStatus(); fetchAttrs(); }}
                />
              </CardContent>
            </Card>
          )}

          {/* Attribute overview */}
          <Card className="border border-border">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Tag size={16} /> Attributter og termer ({attrs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 max-h-[500px] overflow-y-auto">
              {attrs.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  Ingen attributter synket. Kjør sync fra SEO-side eller via <code className="bg-muted px-1 rounded">/api/attributes/sync</code>.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {attrs.map((attr) => {
                    const terms = attr.terms || [];
                    const missing = terms.filter((t) => !t.description || !t.meta_title).length;
                    const optimized = terms.filter((t) => t.modified_locally || t.last_pushed).length;
                    return (
                      <div key={attr.id} className="p-3" data-testid={`attr-row-${attr.id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium">{attr.name}</p>
                            <div className="flex gap-2 mt-1 text-xs">
                              <Badge variant="outline">{terms.length} termer</Badge>
                              <Badge variant="secondary" className={missing > 0 ? 'text-destructive' : 'text-success'}>
                                {missing} mangler SEO
                              </Badge>
                              {optimized > 0 && <Badge variant="default" className="gap-1"><CheckCircle size={10} /> {optimized} optimalisert</Badge>}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setTermAttrFilter(String(attr.id)); toast.info(`Filtrert til ${attr.name}`); }}
                            data-testid={`filter-attr-${attr.id}`}
                          >
                            Filtrer
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Category edit dialog */}
      <Dialog open={!!editingCat} onOpenChange={(o) => !o && setEditingCat(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cat-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Rediger kategori: {editingCat?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-muted-foreground">
                Meta-tittel <span className="opacity-60">({(catForm.meta_title || '').length}/60)</span>
              </Label>
              <Input value={catForm.meta_title || ''} onChange={(e) => setCatForm({ ...catForm, meta_title: e.target.value })} data-testid="cat-meta-title-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-muted-foreground">
                Meta-beskrivelse <span className="opacity-60">({(catForm.meta_description || '').length}/155)</span>
              </Label>
              <Textarea rows={2} value={catForm.meta_description || ''} onChange={(e) => setCatForm({ ...catForm, meta_description: e.target.value })} data-testid="cat-meta-desc-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-muted-foreground">Beskrivelse (HTML)</Label>
              <Textarea rows={8} value={catForm.description || ''} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} className="font-mono text-xs" data-testid="cat-desc-input" />
            </div>
            {catSuggestions && (
              <Card className="border-primary/30 bg-primary/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2"><Sparkle size={14} className="text-primary" /> AI-forslag</span>
                    <Button size="sm" onClick={applyCatSuggestions} data-testid="apply-ai-btn">Sett inn</Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-xs">
                  <div><strong>Meta-tittel:</strong> {catSuggestions.meta_title}</div>
                  <div><strong>Meta-beskrivelse:</strong> {catSuggestions.meta_description}</div>
                  <div>
                    <strong>Beskrivelse:</strong>
                    <div className="mt-1 p-2 bg-background rounded border border-border prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: catSuggestions.description || '' }} />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditingCat(null)}>Avbryt</Button>
            <Button variant="outline" onClick={generateCatAi} disabled={catGenerating} className="gap-1" data-testid="cat-ai-btn">
              <Sparkle size={14} /> {catGenerating ? 'Genererer...' : 'Generer med AI'}
            </Button>
            <Button variant="outline" onClick={saveCatChanges} data-testid="cat-save-btn">Lagre lokalt</Button>
            <Button onClick={pushCatToWoo} data-testid="cat-push-btn" className="gap-1">
              <ArrowsClockwise size={14} /> Lagre & Push til Woo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
